import { useState } from "react";
import { toast } from "sonner";
import { Download, Smartphone, Apple, Monitor, ArrowRight, RefreshCw, Trash2 } from "lucide-react";
import PwaInstallCard from "@/components/PwaInstallCard";
import usePwaInstall from "@/hooks/usePwaInstall";

function StepCard({ icon: Icon, title, steps, note }) {
  return (
    <div className="ih-glass rounded-2xl p-6 flex-1 min-w-[280px]" data-testid={`install-step-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-3 text-lavender-700 dark:text-lavender-300">
        <Icon className="h-5 w-5" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">{title}</span>
      </div>
      <ol className="space-y-2 text-sm">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-2">
            <span className="font-mono text-xs bg-lavender-100 dark:bg-lavender-950/50 text-lavender-700 dark:text-lavender-200 rounded-full h-5 w-5 flex items-center justify-center flex-shrink-0">{i + 1}</span>
            <span dangerouslySetInnerHTML={{ __html: s }} />
          </li>
        ))}
      </ol>
      {note && <p className="text-xs text-muted-foreground mt-3">{note}</p>}
    </div>
  );
}

export default function Installa() {
  const { canInstall, installed, isIosSafari, install } = usePwaInstall();
  const [busy, setBusy] = useState(false);
  const url = typeof window !== "undefined" ? window.location.origin : "";

  const doInstall = async () => {
    setBusy(true);
    try {
      const ok = await install();
      if (ok) toast.success("Installazione completata");
      else toast.info("Installazione annullata");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="installa-page">
      <header className="space-y-1">
        <div className="text-[10px] uppercase tracking-[0.16em] text-lavender-700 dark:text-lavender-300 font-semibold">
          Piattaforma · Installazione
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          Installa IH AuditFlow sul tuo dispositivo
        </h1>
        <p className="text-muted-foreground text-sm max-w-2xl">
          Aggiungi la piattaforma alla schermata Home per aprirla come app nativa, senza barra del browser,
          con aggiornamenti automatici e cache offline.
        </p>
      </header>

      {installed ? (
        <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 px-5 py-4 text-sm text-emerald-800 dark:text-emerald-200" data-testid="pwa-already-installed">
          IH AuditFlow è già installato su questo dispositivo. Buon lavoro.
        </div>
      ) : (
        <PwaInstallCard url={url} variant="wide" />
      )}

      {canInstall && !installed && (
        <div className="flex justify-center">
          <button
            data-testid="pwa-install-page-btn"
            onClick={doInstall}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold bg-lavender-500 hover:bg-lavender-600 text-white shadow-md"
          >
            <Download className="h-4 w-4" />
            Installa ora su questo dispositivo
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        <StepCard
          icon={Smartphone}
          title="Android · Chrome"
          steps={[
            "Apri IH AuditFlow su Chrome",
            'Tocca il menu <span class="inline-block px-1.5 py-0.5 rounded bg-accent/50 text-[10px] font-mono">⋮</span> in alto a destra',
            'Seleziona <strong>Installa app</strong> o <strong>Aggiungi a schermata Home</strong>',
            "Conferma toccando <strong>Installa</strong>",
          ]}
          note="Se non vedi la voce, aggiorna Chrome all'ultima versione."
        />
        <StepCard
          icon={Apple}
          title="iOS · Safari"
          steps={[
            "Apri IH AuditFlow su Safari (non Chrome)",
            'Tocca l\'icona <span class="inline-block px-1.5 py-0.5 rounded bg-accent/50 text-[10px]">Condividi</span> in basso al centro',
            "Scorri e tocca <strong>Aggiungi a schermata Home</strong>",
            'Tocca <strong>Aggiungi</strong> in alto a destra',
          ]}
          note="Chrome iOS non supporta l'installazione: usa Safari."
        />
        <StepCard
          icon={Monitor}
          title="Desktop · Chrome/Edge"
          steps={[
            "Apri IH AuditFlow su Chrome o Edge",
            'Cerca l\'icona <span class="inline-block px-1.5 py-0.5 rounded bg-accent/50 text-[10px]">Installa</span> nella barra degli indirizzi (⊕)',
            "Clicca <strong>Installa</strong>",
            "L'app viene aggiunta al Dock/Start e si apre in finestra dedicata",
          ]}
        />
      </div>

      <section className="ih-glass rounded-2xl p-6 space-y-4">
        <h2 className="font-display text-xl font-semibold">Domande frequenti</h2>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="flex gap-3">
            <div className="h-8 w-8 rounded-full bg-lavender-100 dark:bg-lavender-950/50 text-lavender-700 dark:text-lavender-200 flex items-center justify-center flex-shrink-0">
              <ArrowRight className="h-4 w-4" />
            </div>
            <div>
              <div className="font-medium">Cosa cambia rispetto al browser?</div>
              <div className="text-muted-foreground">L'app si apre a schermo intero, senza barra URL, con icona dedicata sul dispositivo. La sessione resta attiva più a lungo.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="h-8 w-8 rounded-full bg-lavender-100 dark:bg-lavender-950/50 text-lavender-700 dark:text-lavender-200 flex items-center justify-center flex-shrink-0">
              <RefreshCw className="h-4 w-4" />
            </div>
            <div>
              <div className="font-medium">Aggiornamenti automatici?</div>
              <div className="text-muted-foreground">Sì. Il Service Worker (versione attuale <span className="font-mono text-xs">v67</span>) sostituisce silenziosamente la cache alla successiva apertura dell'app dopo un rilascio.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="h-8 w-8 rounded-full bg-lavender-100 dark:bg-lavender-950/50 text-lavender-700 dark:text-lavender-200 flex items-center justify-center flex-shrink-0">
              <Trash2 className="h-4 w-4" />
            </div>
            <div>
              <div className="font-medium">Come si disinstalla?</div>
              <div className="text-muted-foreground">Su Android/iOS tieni premuta l'icona → <strong>Rimuovi/Elimina</strong>. Su Desktop, dal menu dell'app → <strong>Disinstalla</strong>. I dati remoti restano al sicuro.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="h-8 w-8 rounded-full bg-lavender-100 dark:bg-lavender-950/50 text-lavender-700 dark:text-lavender-200 flex items-center justify-center flex-shrink-0">
              <Smartphone className="h-4 w-4" />
            </div>
            <div>
              <div className="font-medium">Serve login anche dopo l'installazione?</div>
              <div className="text-muted-foreground">Sì, la prima volta. La sessione poi resta attiva finché non fai logout esplicito o svuoti la cache.</div>
            </div>
          </div>
        </div>
      </section>

      {isIosSafari && !installed && (
        <div className="rounded-2xl bg-lavender-50 dark:bg-lavender-950/30 border border-lavender-200 dark:border-lavender-900 px-5 py-4 text-sm text-lavender-800 dark:text-lavender-200" data-testid="ios-safari-hint">
          Stai visitando la pagina da Safari su iPhone/iPad. Segui le istruzioni della sezione <strong>iOS · Safari</strong> qui sopra.
        </div>
      )}
    </div>
  );
}
